import {
  Water as HydrationIcon,
  Restaurant as NutritionIcon,
  LocalDining as ProteinIcon,
  Grain as CarbohydratesIcon,
  OilBarrel as FatIcon,
  Science as MicronutrientsIcon,
  Favorite as CholesterolIcon,
} from "@mui/icons-material";
import { Box, Typography, Button, Card, CardContent } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useState, useMemo, useCallback } from "react";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip,
} from "recharts";

import { activityApi } from "apis";
import {
  MetricValuesAggregationRequestDto,
  MetricValuesAggregationRequestDtoIntervalEnum,
  MetricValuesAggregationRequestDtoAggregateTypesEnum,
  MetricValuesAggregationRequestDtoMetricCodeEnum,
  MetricValuesAggregationRequestDtoHealthProviderCodeEnum,
} from "libs/cellera-sdk";
import { useCelleraTheme } from "theme/useCelleraTheme";

interface MetricChartProps {
  patientId: string;
  metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum;
  title: string;
}

type TimeRange = "1D" | "1W" | "1M" | "6M" | "1Y";

interface ChartDataPoint {
  date: string;
  max: number;
  min: number;
  latest: number;
}

const MetricChart: React.FC<MetricChartProps> = ({
  patientId,
  metricCode,
  title,
}) => {
  const { colors } = useCelleraTheme();
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>("1W");

  const getMetricIcon = useCallback(() => {
    const iconProps = { sx: { color: colors.primary.main, fontSize: 20 } };

    switch (metricCode) {
      case MetricValuesAggregationRequestDtoMetricCodeEnum.Hydration:
        return <HydrationIcon {...iconProps} />;
      case MetricValuesAggregationRequestDtoMetricCodeEnum.Calories:
        return <NutritionIcon {...iconProps} />;
      case MetricValuesAggregationRequestDtoMetricCodeEnum.Protein:
        return <ProteinIcon {...iconProps} />;
      case MetricValuesAggregationRequestDtoMetricCodeEnum.Carbohydrates:
        return <CarbohydratesIcon {...iconProps} />;
      case MetricValuesAggregationRequestDtoMetricCodeEnum.TotalFat:
      case MetricValuesAggregationRequestDtoMetricCodeEnum.SaturatedFat:
      case MetricValuesAggregationRequestDtoMetricCodeEnum.MonounsaturatedFat:
      case MetricValuesAggregationRequestDtoMetricCodeEnum.PolyunsaturatedFat:
        return <FatIcon {...iconProps} />;
      case MetricValuesAggregationRequestDtoMetricCodeEnum.Cholesterol:
        return <CholesterolIcon {...iconProps} />;
      case MetricValuesAggregationRequestDtoMetricCodeEnum.Micronutrients:
        return <MicronutrientsIcon {...iconProps} />;
      default:
        return <NutritionIcon {...iconProps} />;
    }
  }, [metricCode, colors.primary.main]);

  const getDateRange = useCallback((timeRange: TimeRange) => {
    const endDate = new Date();
    const startDate = new Date();

    switch (timeRange) {
      case "1D":
        startDate.setDate(endDate.getDate() - 1);
        break;
      case "1W":
        startDate.setDate(endDate.getDate() - 7);
        break;
      case "1M":
        startDate.setMonth(endDate.getMonth() - 1);
        break;
      case "6M":
        startDate.setMonth(endDate.getMonth() - 6);
        break;
      case "1Y":
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
    }

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  }, []);

  const { startDate, endDate } = getDateRange(selectedTimeRange);

  const { data: metricData, isLoading } = useQuery({
    queryKey: [`${metricCode}Chart`, patientId, selectedTimeRange],
    queryFn: () => {
      if (!patientId) return null;

      const request: MetricValuesAggregationRequestDto = {
        metricCode,
        healthProviderCode:
          MetricValuesAggregationRequestDtoHealthProviderCodeEnum.AppleHealthkit,
        interval: MetricValuesAggregationRequestDtoIntervalEnum.Daily,
        aggregateTypes: [
          MetricValuesAggregationRequestDtoAggregateTypesEnum.Sum,
          MetricValuesAggregationRequestDtoAggregateTypesEnum.Min,
          MetricValuesAggregationRequestDtoAggregateTypesEnum.Max,
          MetricValuesAggregationRequestDtoAggregateTypesEnum.LatestRecord,
        ],
        startDate,
        endDate,
        orderBy: "ASC",
      };

      return activityApi.getMetricValuesAggregatedByPatientId({
        patientId,
        metricValuesAggregationRequestDto: request,
      });
    },
    enabled: !!patientId,
    retry: 2,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const extractNumericValue = useCallback((data: unknown): number => {
    if (typeof data === "number") {
      return data;
    }

    if (data && typeof data === "object" && !Array.isArray(data)) {
      const numericValues = Object.values(data).filter(
        (value): value is number => typeof value === "number",
      );

      if (numericValues.length > 0) {
        return numericValues.reduce((sum, value) => sum + value, 0);
      }
    }

    return 0;
  }, []);

  const chartData: ChartDataPoint[] = useMemo(() => {
    if (!metricData?.data) return [];

    return metricData.data.map((item) => ({
      date: new Date(item.startDate).toLocaleDateString("en-US", {
        weekday: "short",
      }),
      max:
        extractNumericValue(item.valueJsonData?.max) ||
        extractNumericValue(item.valueFloatData?.max) ||
        extractNumericValue(item.valueIntData?.max) ||
        0,
      min:
        extractNumericValue(item.valueJsonData?.min) ||
        extractNumericValue(item.valueFloatData?.min) ||
        extractNumericValue(item.valueIntData?.min) ||
        0,
      latest:
        extractNumericValue(item.valueJsonData?.latest) ||
        extractNumericValue(item.valueFloatData?.latest) ||
        extractNumericValue(item.valueIntData?.latest) ||
        0,
    }));
  }, [metricData, extractNumericValue]);

  const timeRangeButtons: TimeRange[] = useMemo(
    () => ["1D", "1W", "1M", "6M", "1Y"],
    [],
  );

  const handleTimeRangeChange = useCallback((range: TimeRange) => {
    setSelectedTimeRange(range);
  }, []);

  if (isLoading) {
    return (
      <Card
        sx={{
          border: "1px solid",
          borderColor: colors.border.light,
          borderRadius: 2,
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Typography>Loading {title.toLowerCase()} data...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        border: "1px solid",
        borderColor: colors.border.light,
        borderRadius: 2,
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        width: "351px",
        height: "516px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent
        sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {getMetricIcon()}
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ fontSize: "0.875rem", fontWeight: 500 }}
            >
              {title}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 0.5 }}>
            {timeRangeButtons.map((range) => (
              <Button
                key={range}
                size="small"
                variant={selectedTimeRange === range ? "contained" : "text"}
                onClick={() => handleTimeRangeChange(range)}
                sx={{
                  minWidth: "auto",
                  px: 1,
                  py: 0.5,
                  fontSize: "0.75rem",
                  backgroundColor:
                    selectedTimeRange === range
                      ? colors.primary.light
                      : "transparent",
                  color:
                    selectedTimeRange === range
                      ? colors.primary.contrast
                      : colors.text.secondary,
                  "&:hover": {
                    backgroundColor:
                      selectedTimeRange === range
                        ? colors.primary.main
                        : colors.primary.light,
                  },
                }}
              >
                {range}
              </Button>
            ))}
          </Box>
        </Box>

        <Box sx={{ height: 400, width: "100%" }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: colors.text.secondary }}
              />
              <YAxis
                yAxisId="left"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: colors.text.secondary }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: colors.text.secondary }}
              />
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: `1px solid ${colors.border.light}`,
                  borderRadius: "8px",
                  fontSize: "12px",
                }}
                formatter={(value: number, name: string) => [
                  `${value}`,
                  name === "max" ? "Max" : "Min",
                ]}
                labelFormatter={(label) => `Date: ${label}`}
              />
              <Legend wrapperStyle={{ fontSize: "12px" }} iconType="rect" />
              <Bar
                yAxisId="left"
                dataKey="max"
                fill={colors.primary.main}
                name="Max"
                radius={[2, 2, 0, 0]}
                barSize={20}
              />
              <Bar
                yAxisId="right"
                dataKey="min"
                fill="#20B2AA"
                name="Min"
                radius={[2, 2, 0, 0]}
                barSize={20}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MetricChart;
